import { saveSensitiveData, getSensitiveData, removeSensitiveData } from './secureStorage';

const TOKEN_KEY = 'authToken';
const TOKEN_EXPIRY_KEY = 'authTokenExpiry';

/**
 * Store authentication token in secure storage
 * @param token - JWT token
 * @param expiresIn - Token expiration time in seconds (optional)
 */
export const setToken = async (token: string, expiresIn?: number): Promise<void> => {
  await saveSensitiveData(TOKEN_KEY, token);
  
  if (expiresIn) {
    const expiryTime = Date.now() + expiresIn * 1000;
    await saveSensitiveData(TOKEN_EXPIRY_KEY, expiryTime.toString());
  }
};

/**
 * Get authentication token from secure storage
 * @returns Token string or null if not found
 */
export const getToken = async (): Promise<string | null> => {
  return await getSensitiveData(TOKEN_KEY);
};

export const removeToken = async (): Promise<void> => {
  await removeSensitiveData(TOKEN_KEY);
  await removeSensitiveData(TOKEN_EXPIRY_KEY);
};

/**
 * Check if token exists
 * @returns True if token exists
 */
export const hasToken = async (): Promise<boolean> => {
  return !!(await getToken());
};

/**
 * Check if token is expired
 * @returns True if token is expired
 */
export const isTokenExpired = async (): Promise<boolean> => {
  const expiryTime = await getSensitiveData(TOKEN_EXPIRY_KEY);
  
  if (!expiryTime) {
    return false; // If no expiry time set, assume token is valid
  }
  
  return Date.now() > parseInt(expiryTime, 10);
};

/**
 * Get token expiry time
 * @returns Expiry timestamp or null
 */
export const getTokenExpiry = async (): Promise<number | null> => {
  const expiryTime = await getSensitiveData(TOKEN_EXPIRY_KEY);
  return expiryTime ? parseInt(expiryTime, 10) : null;
};

const tokenUtilsMethods = {
  setToken,
  getToken,
  removeToken,
  hasToken,
  isTokenExpired,
  getTokenExpiry,
};

export default tokenUtilsMethods;
